import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { useCreatePage } from "@/features/content/hooks/useContentMutations";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";
import { departmentRequiresNewsPage } from "@/features/departments/utils/department-news";
import type { CreatePageRequest, LocalizedText } from "@/types/content";

const CONTENT_TYPE = "pages";

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const emptyLocalized = (): LocalizedText => ({ ar: "", en: "", ku: "" });

export function CreatePageDialog() {
  const [open, setOpen] = useState(false);
  const [titleEn, setTitleEn] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [newsDescription, setNewsDescription] = useState<LocalizedText>(emptyLocalized());
  const [departmentId, setDepartmentId] = useState("");
  const [isNews, setIsNews] = useState(false);
  const [publishedDate, setPublishedDate] = useState("");

  const createPage = useCreatePage();
  const { data: deptData } = useDepartments({ limit: 100 });
  const departments = deptData?.items ?? [];
  const selectedDepartment = useMemo(
    () => departments.find((d) => d.id === departmentId),
    [departments, departmentId],
  );
  const newsPageLocked = departmentRequiresNewsPage(selectedDepartment);

  useEffect(() => {
    if (newsPageLocked) {
      setIsNews(true);
    }
  }, [newsPageLocked]);

  const reset = () => {
    setTitleEn("");
    setTitleAr("");
    setSlug("");
    setSlugTouched(false);
    setNewsDescription(emptyLocalized());
    setDepartmentId("");
    setIsNews(false);
    setPublishedDate("");
  };

  const handleTitleEnChange = (value: string) => {
    setTitleEn(value);
    if (!slugTouched) {
      setSlug(slugify(value));
    }
  };

  const handleSubmit = async () => {
    const nextTitleEn = titleEn.trim();
    const nextTitleAr = titleAr.trim();
    const nextSlug = slug.trim();

    if (!nextTitleEn || !nextTitleAr || !nextSlug || !departmentId) return;

    const payload: CreatePageRequest = {
      title: {
        ar: nextTitleAr,
        en: nextTitleEn,
        ku: nextTitleAr,
      },
      slug: {
        ar: nextSlug,
        en: nextSlug,
        ku: nextSlug,
      },
      departmentId,
      isNews,
      publishedDate: isNews && publishedDate ? publishedDate : undefined,
      newsDescription:
        isNews && newsDescription.ar?.trim()
          ? {
              ar: newsDescription.ar.trim(),
              en: newsDescription.en?.trim() || newsDescription.ar.trim(),
              ku: newsDescription.ku?.trim() || newsDescription.ar.trim(),
            }
          : undefined,
    };

    await createPage.mutateAsync({ contentType: CONTENT_TYPE, data: payload });
    reset();
    setOpen(false);
  };

  const canSubmit =
    Boolean(titleEn.trim()) &&
    Boolean(titleAr.trim()) &&
    Boolean(slug.trim()) &&
    Boolean(departmentId);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" size="sm">
          <Plus className="size-4" />
          إنشاء صفحة
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader className="text-right">
          <DialogTitle>إنشاء صفحة جديدة</DialogTitle>
          <DialogDescription>
            أنشئ هيكل الصفحة بلغتين. يمكن تحرير المحتوى لاحقًا في نظام إدارة المحتوى (CMS).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>الدائرة</Label>
            <Select value={departmentId} onValueChange={setDepartmentId}>
              <SelectTrigger>
                <SelectValue placeholder="اختر الدائرة" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.nameAr ?? d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="space-y-0.5 text-right">
              <Label htmlFor="is-news">صفحة خبر</Label>
              {newsPageLocked && (
                <p className="text-xs text-muted-foreground">
                  مطلوب لصفحات قسم الاعلام
                </p>
              )}
            </div>
            <Switch
              id="is-news"
              checked={isNews}
              onCheckedChange={setIsNews}
              disabled={newsPageLocked}
            />
          </div>

          {isNews && (
            <div className="space-y-2">
              <Label htmlFor="published-date">تاريخ النشر</Label>
              <Input
                id="published-date"
                type="datetime-local"
                value={publishedDate}
                onChange={(e) => setPublishedDate(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="title-en">
              Title (English) <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title-en"
              value={titleEn}
              onChange={(e) => handleTitleEnChange(e.target.value)}
              placeholder="Page title"
              dir="ltr"
              className="text-left"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="title-ar">
              العنوان (العربية) <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title-ar"
              value={titleAr}
              onChange={(e) => setTitleAr(e.target.value)}
              placeholder="عنوان الصفحة"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="page-slug">
              الرابط (Slug) <span className="text-destructive">*</span>
            </Label>
            <Input
              id="page-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              placeholder="page-slug"
              dir="ltr"
              className="text-left"
            />
          </div>

          {isNews && (
            <>
              <div className="space-y-2">
                <Label htmlFor="news-description-en">News description (English)</Label>
                <Input
                  id="news-description-en"
                  value={newsDescription.en ?? ""}
                  onChange={(e) =>
                    setNewsDescription((prev) => ({ ...prev, en: e.target.value }))
                  }
                  placeholder="Short description"
                  dir="ltr"
                  className="text-left"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="news-description-ar">وصف الخبر (العربية)</Label>
                <Input
                  id="news-description-ar"
                  value={newsDescription.ar}
                  onChange={(e) =>
                    setNewsDescription((prev) => ({ ...prev, ar: e.target.value }))
                  }
                  placeholder="وصف مختصر"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-start">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || createPage.isPending}
          >
            {createPage.isPending ? <Spinner className="size-4" /> : "إنشاء"}
          </Button>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            إلغاء
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
